import React, { useEffect, useState } from 'react'
import { Theme, ThemeContext } from './ThemeContext'

interface ThemeProviderProps {
  /**
   * Default theme
   */
  defaultTheme?: Theme
  
  /**
   * Local storage key for theme persistence
   */
  storageKey?: string
  
  /**
   * Children components
   */
  children: React.ReactNode
}

/**
 * Theme provider for managing light/dark theme state
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  defaultTheme = 'system',
  storageKey = 'qlik-script-editor-theme',
  children
}) => {
  const [theme, setThemeState] = useState<Theme>(defaultTheme)
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    // Load theme from localStorage
    const stored = localStorage.getItem(storageKey) as Theme
    if (stored) {
      setThemeState(stored)
    }
  }, [storageKey])

  useEffect(() => {
    // Update actual theme based on theme setting
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      setActualTheme(mediaQuery.matches ? 'dark' : 'light')
      
      const handleChange = (e: MediaQueryListEvent) => {
        setActualTheme(e.matches ? 'dark' : 'light')
      }
      
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    } else {
      setActualTheme(theme)
    }
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem(storageKey, newTheme)
  }

  const value = {
    theme,
    actualTheme,
    setTheme
  }

  return (
    <ThemeContext.Provider value={value}>
      <div className={`qlik-script-editor-theme qlik-script-editor-theme--${actualTheme}`}>
        {children}
      </div>
    </ThemeContext.Provider>
  )
}

